import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, RefreshCw, ServerCrash, TriangleAlert } from 'lucide-react';
import { api, ApiError } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Pill } from '../componentes/ui/Pill.js';

interface Divergencia {
  tipo: 'zona_ausente' | 'zona_orfa' | 'nos_divergentes';
  zona: string;
  detalhe: string;
  bloco?: { id: string; cidr: string };
}

interface Diferenca {
  verificadoEm: string;
  zonasNoPainel: number;
  zonasNoPowerDns: number;
  divergencias: Divergencia[];
  nos: { no: string; ok: boolean; erro?: string }[];
}

const TIPOS = {
  zona_ausente: { rotulo: 'Faltando no PowerDNS', tom: 'erro' as const },
  zona_orfa: { rotulo: 'Órfã no PowerDNS', tom: 'aviso' as const },
  nos_divergentes: { rotulo: 'Nó não respondeu', tom: 'erro' as const },
};

export function Sincronizacao() {
  const qc = useQueryClient();

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['sincronizacao'],
    queryFn: () => api.get<Diferenca>('/sincronizacao'),
  });

  const recriar = useMutation({
    mutationFn: () => api.post<{ criadas: string[]; falhas: string[] }>('/sincronizacao/recriar-ausentes'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sincronizacao'] });
      void qc.invalidateQueries({ queryKey: ['zonas'] });
    },
  });

  if (isLoading || !data) return <Esqueleto />;

  const ausentes = data.divergencias.filter((d) => d.tipo === 'zona_ausente');
  const limpo = data.divergencias.length === 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[13px] text-texto-suave">
          Última verificação:{' '}
          {new Date(data.verificadoEm).toLocaleString('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'medium',
          })}
        </p>
        <Botao
          iconeEsquerda={<RefreshCw size={15} />}
          onClick={() => void refetch()}
          carregando={isFetching}
        >
          Verificar de novo
        </Botao>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Numero rotulo="Zonas no painel" valor={data.zonasNoPainel} />
        <Numero rotulo="Zonas no PowerDNS" valor={data.zonasNoPowerDns} />
        <Numero
          rotulo="Divergências"
          valor={data.divergencias.length}
          alerta={data.divergencias.length > 0}
        />
      </div>

      {limpo ? (
        <div className="flex items-start gap-3 rounded-card border border-sucesso/20 bg-sucesso-fundo px-4 py-4 text-sucesso">
          <CheckCircle2 size={20} className="mt-0.5 shrink-0" />
          <div className="text-[13px]">
            <p className="font-extrabold">Painel e PowerDNS estão de acordo</p>
            <p className="mt-0.5 opacity-90">
              Toda zona conhecida pelo painel existe no PowerDNS, e não há zonas sem dono.
            </p>
          </div>
        </div>
      ) : (
        <>
          {ausentes.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-erro/20 bg-erro-fundo px-4 py-3">
              <p className="text-[13px] text-erro">
                <strong>{ausentes.length}</strong> zona(s) do painel não existem no PowerDNS. Podem
                ser recriadas com segurança — a operação só cria o que falta.
              </p>
              <Botao
                variante="primario"
                onClick={() => recriar.mutate()}
                carregando={recriar.isPending}
              >
                Recriar zonas faltantes
              </Botao>
            </div>
          )}

          {recriar.data && (
            <p className="rounded-card border border-sucesso/20 bg-sucesso-fundo px-4 py-2.5 text-[13px] text-sucesso">
              {recriar.data.criadas.length} zona(s) recriadas
              {recriar.data.falhas.length > 0 && ` · ${recriar.data.falhas.length} falharam`}.
            </p>
          )}
          {recriar.error && (
            <p className="rounded-card border border-erro/20 bg-erro-fundo px-4 py-2.5 text-[13px] text-erro">
              {(recriar.error as ApiError).mensagem}
            </p>
          )}

          <div className="overflow-hidden rounded-card border border-borda bg-superficie">
            <ul className="divide-y divide-borda-suave">
              {data.divergencias.map((d, i) => (
                <li key={i} className="flex items-start gap-3 px-[18px] py-3">
                  <TriangleAlert
                    size={16}
                    className={`mt-0.5 shrink-0 ${
                      TIPOS[d.tipo].tom === 'erro' ? 'text-erro' : 'text-aviso'
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-[13px] font-semibold">{d.zona}</span>
                      <Pill tom={TIPOS[d.tipo].tom}>{TIPOS[d.tipo].rotulo}</Pill>
                      {d.bloco && (
                        <span className="font-mono text-[11.5px] text-texto-suave">
                          {d.bloco.cidr}
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 text-[12.5px] leading-snug text-texto-suave">
                      {d.detalhe}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Dito explicitamente: a ausência de um botão "corrigir tudo" é
              intencional, não uma funcionalidade que faltou. */}
          <p className="rounded-card border border-borda bg-superficie px-4 py-3 text-[12.5px] leading-snug text-texto-suave">
            Zonas órfãs não são removidas automaticamente. Cada uma pode ser lixo de um bloco
            excluído ou parte de uma migração em andamento — apagar sem contexto destruiria o
            trabalho de alguém. Remova pelo PowerDNS quando tiver certeza.
          </p>
        </>
      )}

      <section>
        <h2 className="mb-2 text-[14px] font-extrabold">PowerDNS por nó</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.nos.map((n) => (
            <div
              key={n.no}
              className={`flex items-start gap-2.5 rounded-card border px-4 py-3 ${
                n.ok ? 'border-borda bg-superficie' : 'border-erro/20 bg-erro-fundo'
              }`}
            >
              {n.ok ? (
                <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-sucesso" />
              ) : (
                <ServerCrash size={16} className="mt-0.5 shrink-0 text-erro" />
              )}
              <div className="min-w-0">
                <p className="text-[13px] font-bold">{n.no}</p>
                <p className={`text-[11.5px] ${n.ok ? 'text-texto-suave' : 'text-erro'}`}>
                  {n.ok ? 'respondendo' : (n.erro ?? 'sem resposta')}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function Numero({
  rotulo,
  valor,
  alerta,
}: {
  rotulo: string;
  valor: number;
  alerta?: boolean;
}) {
  return (
    <div
      className={`rounded-card border p-4 ${
        alerta ? 'border-erro/20 bg-erro-fundo' : 'border-borda bg-superficie'
      }`}
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">{rotulo}</p>
      <p
        className={`mt-1 font-mono text-[26px] font-extrabold leading-none ${
          alerta ? 'text-erro' : ''
        }`}
      >
        {valor}
      </p>
    </div>
  );
}

function Esqueleto() {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-card border border-borda bg-superficie" />
        ))}
      </div>
      <div className="h-48 animate-pulse rounded-card border border-borda bg-superficie" />
    </div>
  );
}
